/**
 * ThemeContext - Dark/Light mode toggle for the dashboard.
 * - Defaults to dark mode
 * - Persists preference in localStorage (instant) AND profiles.theme_preference (cross-device)
 * - On load: uses localStorage first, then fetches from profile and syncs
 */
import { createContext, useContext, useEffect, useState, useCallback, type ReactNode } from 'react';
import { supabase } from '@/integrations/supabase/client';

type Theme = 'dark' | 'light';

interface ThemeContextValue {
 theme: Theme;
 setTheme: (theme: Theme) => void;
 toggleTheme: () => void;
}

const ThemeContext = createContext<ThemeContextValue | undefined>(undefined);

const STORAGE_KEY = 'ampa-theme-preference';

function getStoredTheme(): Theme {
 try {
 const stored = localStorage.getItem(STORAGE_KEY);
 if (stored === 'light') return 'light';
 } catch {}
 return 'dark';
}

function applyThemeToDOM(theme: Theme) {
 const root = document.documentElement;
 if (theme === 'light') {
 root.setAttribute('data-theme', 'light');
 root.classList.remove('dark');
 } else {
 root.removeAttribute('data-theme');
 root.classList.add('dark');
 }
}

export function ThemeProvider({ children }: { children: ReactNode }) {
 const [theme, setThemeState] = useState<Theme>(getStoredTheme);
 const [profileLoaded, setProfileLoaded] = useState(false);

 // Apply theme to DOM immediately
 useEffect(() => {
 applyThemeToDOM(theme);
 }, [theme]);

 // On mount: fetch profile preference and sync if different from localStorage
 useEffect(() => {
 let cancelled = false;

 async function syncFromProfile() {
 try {
 const { data: { user } } = await supabase.auth.getUser();
 if (!user || cancelled) return;

 const { data } = await supabase
 .from('profiles')
 .select('theme_preference')
 .eq('user_id', user.id)
 .single();

 if (cancelled) return;

 const profileTheme = data?.theme_preference as Theme | null;
 const localTheme = getStoredTheme();

 if (profileTheme && (profileTheme === 'light' || profileTheme === 'dark')) {
 // If no local preference was explicitly set, use profile
 if (!localStorage.getItem(STORAGE_KEY)) {
 setThemeState(profileTheme);
 localStorage.setItem(STORAGE_KEY, profileTheme);
 } else if (localTheme !== profileTheme) {
 // Local takes precedence but sync it back to profile
 await supabase
 .from('profiles')
 .update({ theme_preference: localTheme })
 .eq('user_id', user.id);
 }
 }
 } catch (err) {
 console.error('[ThemeContext] Error syncing theme from profile:', err);
 } finally {
 if (!cancelled) setProfileLoaded(true);
 }
 }

 syncFromProfile();
 return () => { cancelled = true; };
 }, []);

 const setTheme = useCallback((newTheme: Theme) => {
 setThemeState(newTheme);
 localStorage.setItem(STORAGE_KEY, newTheme);
 applyThemeToDOM(newTheme);

 // Save to profile in background
 supabase.auth.getUser().then(({ data: { user } }) => {
 if (user) {
 supabase
 .from('profiles')
 .update({ theme_preference: newTheme })
 .eq('user_id', user.id)
 .then(({ error }) => {
 if (error) console.error('[ThemeContext] Error saving theme to profile:', error);
 });
 }
 });
 }, []);

 const toggleTheme = useCallback(() => {
 setTheme(theme === 'dark' ? 'light' : 'dark');
 }, [theme, setTheme]);

 return (
 <ThemeContext.Provider value={{ theme, setTheme, toggleTheme }}>
 {children}
 </ThemeContext.Provider>
 );
}

export function useTheme() {
 const ctx = useContext(ThemeContext);
 if (!ctx) throw new Error('useTheme must be used within ThemeProvider');
 return ctx;
}
